/**
 * Message keys and display texts shared by the inspector sections (W2-06; DESIGN.md §2.5 vocabulary, §6.9
 * property catalogue): property names per line kind ("Length" on a normal line, "Image size X" on an image line),
 * unit suffixes (friendly and RPE raw), line types, HUD elements, text modes and curve names (plain words in
 * Guided, RPE names in Full).
 */

import type { LineTypeChoice } from '../../core/commands/types';
import { Ease } from '../../core/ease/index';
import type { EaseShapeWord, EaseStrength, Ease as EaseValue } from '../../core/ease/types';
import type { FriendlyUnit, LineKind, LineProp, UiElement } from '../../core/model/types';
import type { Locale } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { ExperienceLevel } from '../../state/types';
import { m } from './inspector.messages';
import type { TextMode } from './model';

type T = (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string;

/**
 * Display texts as one list with the separators and word order of `locale` ("3 taps, 2 holds"; "the image and the
 * text keyframes" with `conjunction`).
 */
export function fmtList(locale: Locale, items: readonly string[], type: 'conjunction' | 'unit' = 'unit'): string {
  return new Intl.ListFormat(locale, { style: type === 'unit' ? 'short' : 'long', type }).format(items);
}

/** Name of a line property as the inspector shows it on a line of `kind`. */
export function propLabelKey(prop: LineProp, kind: LineKind, uiMover: boolean): MessageKey {
  switch (prop) {
    case 'moveX':
      return m.propMoveX;
    case 'moveY':
      return m.propMoveY;
    case 'rotate':
      return m.propRotate;
    case 'alpha':
      return m.propAlpha;
    case 'speed':
      return m.propSpeed;
    case 'color':
      return m.propColor;
    case 'text':
      return m.propText;
    case 'incline':
      return m.propIncline;
    case 'paint':
      return m.propPaint;
    case 'gif':
      return m.propGif;
    case 'scaleX':
    case 'scaleY': {
      const x = prop === 'scaleX';
      if (uiMover) return x ? m.propUiSizeX : m.propUiSizeY;
      switch (kind) {
        case 'normal':
          return x ? m.propLength : m.propThickness;
        case 'texture':
        case 'gif':
          return x ? m.propImageSizeX : m.propImageSizeY;
        case 'text':
          return x ? m.propTextSizeX : m.propTextSizeY;
        case 'paint':
          return x ? m.propBrushWidth : m.propBrushHeight;
      }
    }
  }
}

/** Unit suffixes of a friendly unit: what the field shows in friendly mode and in RPE raw mode. */
export function unitLabels(t: T, unit: FriendlyUnit): { readonly friendly: string; readonly raw: string } {
  switch (unit) {
    case 'px':
    case 'brushPx':
      return { friendly: t(m.px), raw: t(m.px) };
    case 'deg':
      return { friendly: t(m.deg), raw: t(m.deg) };
    case 'opacityPercent':
      return { friendly: t(m.percent), raw: '0-255' };
    case 'lengthPercent':
    case 'sizePercent':
      return { friendly: t(m.percent), raw: t(m.times) };
    case 'progressPercent':
      return { friendly: t(m.percent), raw: '0-1' };
    case 'thickness':
      return { friendly: t(m.times), raw: t(m.times) };
    case 'speed':
    case 'raw':
      return { friendly: '', raw: '' };
  }
}

/** Field step in the shown unit. */
export function unitStep(unit: FriendlyUnit, raw: boolean): number {
  if (raw) {
    switch (unit) {
      case 'opacityPercent':
      case 'px':
      case 'deg':
      case 'brushPx':
        return 1;
      case 'speed':
        return 0.5;
      default:
        return 0.01;
    }
  }
  switch (unit) {
    case 'thickness':
      return 0.1;
    case 'speed':
      return 0.5;
    default:
      return 1;
  }
}

export const LINE_TYPE_LABEL: Readonly<Record<LineTypeChoice, MessageKey>> = {
  line: m.typeLine,
  text: m.typeText,
  image: m.typeImage,
  gif: m.typeGif,
  paint: m.typePaint,
  uiMover: m.typeUiMover,
};

export const LINE_TYPE_ICON: Readonly<Record<LineTypeChoice, string>> = {
  line: 'Minus',
  text: 'Type',
  image: 'Image',
  gif: 'Film',
  paint: 'Brush',
  uiMover: 'LayoutDashboard',
};

export const UI_ELEMENT_LABEL: Readonly<Record<UiElement, MessageKey>> = {
  pause: m.uiPause,
  combonumber: m.uiCombonumber,
  combo: m.uiCombo,
  score: m.uiScore,
  bar: m.uiBar,
  name: m.uiName,
  level: m.uiLevel,
};

export const UI_ELEMENT_ICON: Readonly<Record<UiElement, string>> = {
  pause: 'Pause',
  combonumber: 'Hash',
  combo: 'Tally5',
  score: 'Trophy',
  bar: 'GalleryHorizontalEnd',
  name: 'Music',
  level: 'Gauge',
};

export const TEXT_MODE_LABEL: Readonly<Record<TextMode, MessageKey>> = {
  switch: m.modeSwitch,
  typeOn: m.modeTypeOn,
  erase: m.modeErase,
  extend: m.modeExtend,
  counter: m.modeCounter,
};

export const TEXT_MODE_DESC: Readonly<Record<TextMode, MessageKey>> = {
  switch: m.modeSwitchDesc,
  typeOn: m.modeTypeOnDesc,
  erase: m.modeEraseDesc,
  extend: m.modeExtendDesc,
  counter: m.modeCounterDesc,
};

const STRENGTH: Readonly<Record<EaseStrength, MessageKey>> = {
  gentle: m.strengthGentle,
  soft: m.strengthSoft,
  medium: m.strengthMedium,
  strong: m.strengthStrong,
  stronger: m.strengthStronger,
  extreme: m.strengthExtreme,
  sharp: m.strengthSharp,
  overshoot: m.strengthOvershoot,
  springy: m.strengthSpringy,
  bouncy: m.strengthBouncy,
};

const SHAPE: Readonly<Record<EaseShapeWord, MessageKey>> = {
  slowStart: m.shapeSlowStart,
  slowEnd: m.shapeSlowEnd,
  slowBoth: m.shapeSlowBoth,
};

/** Structural equality of two eases (mixed curve detection). */
export function easeEq(a: EaseValue, b: EaseValue): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'hold' && b.kind === 'hold') return (a.gap === true) === (b.gap === true);
  if (a.kind === 'tween' && b.kind === 'tween') return a.id === b.id && a.left === b.left && a.right === b.right;
  if (a.kind === 'bezier' && b.kind === 'bezier') return a.p.every((v, i) => v === b.p[i]);
  return false;
}

/** "Soft, slow end (RPE 4)" in Guided, "OutQuad (RPE 4)" in Full; slices add "part 0.25 to 1". */
export function curveText(t: T, e: EaseValue, level: ExperienceLevel): string {
  if (e.kind === 'hold') return t(m.curveStay);
  if (e.kind === 'bezier') return t(m.curveBezier);
  const entry = Ease.catalog(e.id);
  let name: string;
  if (e.id === 1 || entry === null) name = t(m.curveLinear);
  else if (level === 'guided' && entry.strength !== null && entry.shape !== null) {
    name = t(m.curveGuided, { strength: t(STRENGTH[entry.strength]), shape: t(SHAPE[entry.shape]), id: e.id });
  } else name = t(m.curveFull, { name: entry.rpeName, id: e.id });
  if (e.left === 0 && e.right === 1) return name;
  return t(m.curveSliced, { name, left: String(e.left), right: String(e.right) });
}
